<template lang="pug">
span.uc-lottie.inline-block(
  :data-autoplay="autoplay ? '' : null"
  :data-src="src"
  ref="elRef"
)
  span(ref="playerRef")
</template>

<script setup lang="ts">
import { nextTick, ref, unref, watch, watchEffect, computed } from 'vue';
import type { AnimationItem, LottiePlayer } from 'lottie-web';
import colors from 'tailwindcss/colors';
import replaceLottieColor from '../lib/replaceLottieColor';
import useThemeChange from '../lib/useThemeChange';
import bugAlert from '../lib/bugAlert';
import useSuspend from '../lib/useSuspend';
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';

const suspend = useSuspend();
const elRef = ref<AnimatedElement>();
const playerRef = ref<HTMLElement>();

interface Props {
  src: string;
  duration?: number;
  scrub?: boolean;
  loop?: boolean;
  autoplay?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  duration: 2,
  autoplay: true,
  scrub: false,
  loop: false
});

const rawJson = ref<string>('');
const json = ref<string>('');
const breakpoints = useBreakpoints(breakpointsTailwind);
const largerThanSm = breakpoints.greater('sm');

// do not play if mobile and a background
const playScreenBackground = computed(() => {
  const isBackground =
    elRef.value?.getAttribute('data-page-background') ||
    elRef.value?.getAttribute('data-screen-background');
  return isBackground && !largerThanSm.value ? false : true;
});

const replaceColors = (raw?: string) => {
  raw = raw || unref(rawJson);
  if (!raw) return;
  let out = raw;
  const el = elRef?.value;

  const primary = props.src.match(/\.(p|primary)-(\w+)\./)?.[2];
  const secondary = props.src.match(/\.(s|secondary)-(\w+)\./)?.[2];
  const gray = 'gray';

  const c = { primary, secondary, gray };

  for (const [colorKey, colorValue] of Object.entries(c)) {
    if (!colorValue) continue;

    for (const [shade, hex] of Object.entries(colors[colorValue])) {
      const cssVar = `--color-${colorKey}-${shade}`;
      const newRgb = el && getComputedStyle(el).getPropertyValue(cssVar);

      if (!newRgb) {
        bugAlert(`Missing CSS variable: ${cssVar}`);
        continue;
      }

      out = replaceLottieColor(out, hex as string, newRgb);
    }
  }

  json.value = out;
};

watchEffect(() => {
  if (!props.src) return;

  fetch(props.src, { cache: 'reload' })
    .then(r => r.json())
    .then(r => {
      rawJson.value = JSON.stringify(r);
    });
});

watch(rawJson, replaceColors);

let playing: boolean;

const init = async () => {
  const el = unref(elRef);
  const player = unref(playerRef);
  const animationData = JSON.parse(unref(json) || 'false');

  if (!el) return;
  if (!player) return;
  if (!animationData) return;

  let anim: AnimationItem;

  let waitingToPlay: boolean;

  const createLottiePlayer = async () => {
    // code splitting
    const lottie = (await import('lottie-web')) as unknown as LottiePlayer;

    const loop = suspend.reducedMotion
      ? false
      : props.src.match(/\bloop-(\d+)\b/) !== null || props.loop;

    return new Promise(resolve => {
      player.innerHTML = '';

      anim = lottie.loadAnimation({
        container: player,
        renderer: 'svg',
        loop,
        autoplay: false,
        animationData,
        rendererSettings: { viewBoxOnly: true }
      });

      anim.addEventListener('complete', () => {
        if (!loop) playing = false;
      });

      const onReady = () => {
        resolve(1);

        if (playing) {
          anim.goToAndPlay(0, true);
        } else {
          anim.goToAndStop(0, true);
        }
      };

      nextTick(() => {
        if (anim.isLoaded) onReady();
      });

      anim.addEventListener('data_ready', onReady);
    });
  };

  const ready = createLottiePlayer();

  el.play = async () => {
    waitingToPlay = true;
    await ready;

    // if (playing) {
    //   // console.log(`Lottie already playing: ${props.src}`);
    //   return;
    // }

    if (waitingToPlay && 'goToAndPlay' in anim) {
      if (suspend.reducedMotion) {
        playing = false;
        anim.goToAndStop(anim?.totalFrames - 1, true);
      } else {
        playing = true;
        anim.goToAndPlay(
          !playScreenBackground.value ? anim?.totalFrames - 1 : 0,
          true
        );
      }
    }
  };

  el.reset = async () => {
    waitingToPlay = false;
    await ready;

    if ('goToAndPlay' in anim) {
      playing = false;
      anim.goToAndStop(
        suspend.reducedMotion || !playScreenBackground.value
          ? anim?.totalFrames - 1
          : 0,
        true
      );
    }
  };

  if (import.meta.hot) {
    import.meta.hot.on('publicwatch:update', async ({ path }) => {
      const name = path.split(/[\\/]/).slice(-1)[0];
      const src = props.src;

      if (!src) return;
      if (!src.includes(name)) return;

      await fetch(src, { cache: 'reload' });
      await createLottiePlayer();

      setTimeout(() => {
        if (!elRef.value) return;
        if (elRef.value.classList.contains('-in')) elRef.value.play();
        else elRef.value.reset();
      }, 100);
    });
  }
};

watchEffect(() => {
  if (json.value) init();
});

watch(() => suspend.reducedMotion, init, { flush: 'post' });

watch(() => largerThanSm, init, { flush: 'post' });

useThemeChange(replaceColors);
</script>
